import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import FormField from "@/components/FormField";
import ImageUploadField from "@/components/ImageUploadField";
import FormFieldWithHint from "@/components/FormFieldWithHint";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const EMPTY_FORM = { title: "", content: "", image_url: "", youtube_url: "", thumbnail_url: "", is_featured: false };

function getFormValues(article) {
  if (!article) return EMPTY_FORM;
  return {
    title: article.title || "",
    content: article.content || "",
    image_url: article.image_url || "",
    youtube_url: article.youtube_url || "",
    thumbnail_url: article.thumbnail_url || "",
    is_featured: article.is_featured || false,
  };
}

function useArticleForm(article, isOpen) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);

  useEffect(() => {
    const vals = getFormValues(article);
    setTitle(vals.title);
    setContent(vals.content);
    setImageUrl(vals.image_url);
    setYoutubeUrl(vals.youtube_url);
    setThumbnailUrl(vals.thumbnail_url);
    setIsFeatured(vals.is_featured);
  }, [article, isOpen]);

  const toPayload = () => ({
    title,
    content,
    image_url: imageUrl || null,
    youtube_url: youtubeUrl || null,
    thumbnail_url: thumbnailUrl || null,
    is_featured: isFeatured,
  });

  return {
    title, setTitle, content, setContent,
    imageUrl, setImageUrl, youtubeUrl, setYoutubeUrl,
    thumbnailUrl, setThumbnailUrl, isFeatured, setIsFeatured,
    toPayload,
  };
}

function getModalTitle(article) {
  if (article?.id) return "Edit Article";
  if (article) return "Import Article";
  return "New Article";
}

function getButtonText(isLoading, article) {
  if (isLoading) return "Saving...";
  if (article?.id) return "Update";
  return "Publish";
}

const ArticleModal = ({ isOpen, onClose, onSave, article }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const form = useArticleForm(article, isOpen);

  const modalTitle = getModalTitle(article);
  const buttonText = getButtonText(isLoading, article);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await onSave(form.toPayload());
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">{modalTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          <FormField id="title" label="Title" value={form.title} onChange={form.setTitle} placeholder="Article title" required />
          <FormField id="content" label="Content" value={form.content} onChange={form.setContent} placeholder="Write your article..." required multiline />
          <ImageUploadField id="image" label="Article Image (upload or paste URL)" value={form.imageUrl} onChange={form.setImageUrl} />
          <FormFieldWithHint id="youtube" label="YouTube URL (optional)" hint="Paste a YouTube video URL to embed it in your article" value={form.youtubeUrl} onChange={form.setYoutubeUrl} placeholder="https://www.youtube.com/watch?v=..." />
          <ImageUploadField id="thumbnail" label="Thumbnail Image (for contents page)" hint="Image shown on the contents page (uses main image if empty)" value={form.thumbnailUrl} onChange={form.setThumbnailUrl} />
          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="isFeatured"
              checked={form.isFeatured}
              onChange={(e) => form.setIsFeatured(e.target.checked)}
              className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="isFeatured" className="text-slate-300 cursor-pointer">Feature on contents page</Label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
            <Button type="submit" data-testid="article-submit-button" disabled={isLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">{buttonText}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleModal;
